/**
 * Shipping Progress
 *
 * Exports updateShippingProgress(totalCents) – updates every .shipping-progress
 * element on the page (cart page, cart panel, PDP, etc.).
 *
 * No event listeners here; callers are responsible for wiring events.
 * theme.js registers a passive cart:count:updated listener.
 */

import { formatMoney } from './utils.js';

const settings = window.cartSettings || {};
const threshold = settings.freeShippingThreshold || 0;
const overMessage = settings.freeShippingOverMessage || '';
const underMessage = settings.freeShippingUnderMessage || '';
const previewMessage = settings.freeShippingPreviewMessage || '';

/**
 * Build a free shipping message string for a given cart total.
 * @param {number} totalCents - Cart total in cents
 * @returns {string} HTML message string (may contain richtext markup)
 */
export function getShippingMessage(totalCents, preview = false) {
    if (!threshold) return '';
    if (totalCents >= threshold) return overMessage;
    const remaining = threshold - totalCents;
    if (preview) {
        return previewMessage.replace('{amt}', formatMoney(remaining));
    }
    return underMessage.replace('{amt}', formatMoney(remaining));
}

/**
 * Apply shipping progress state to a single .shipping-progress element.
 * @param {Element} el - The .shipping-progress element to update
 * @param {number} totalCents - Cart total in cents
 * @param {boolean} [preview] - Use preview message copy
 */
export function applyShippingProgress(el, totalCents, preview = false) {
    if (!threshold) return;
    const widthPercent = Math.min((totalCents / threshold) * 100, 100);
    const isFilled = totalCents >= threshold;
    const messageEl = el.querySelector('.shipping-progress__message');
    if (messageEl) messageEl.innerHTML = getShippingMessage(totalCents, preview);
    const fill = el.querySelector('.shipping-progress__fill');
    if (fill) {
        fill.style.width = `${widthPercent}%`;
        fill.classList.toggle('shipping-progress__fill--filled', isFilled);
    }
}

/**
 * Update all .shipping-progress elements on the page.
 * @param {number} totalCents - Cart total in cents
 */
export function updateShippingProgress(totalCents) {
    if (!threshold) return;
    const elements = document.querySelectorAll('.shipping-progress');
    elements.forEach(el => applyShippingProgress(el, totalCents));
}
